import React, {useEffect, useRef, useState} from "react";
import {Field, FieldsManager} from "../../../data/services/fields";
import FieldsForm from "../../../common/components/fields/fields-form";
import Button from "../../../common/components/button";
import {useDispatch, useSelector} from "react-redux";
import {resendToken, verifyToken} from "../../../data/actions/user";
import ChevronDownIcon from "@heroicons/react/20/solid/ChevronDownIcon";
import {
    TWO_FACTOR_AUTH_TYPE_AUTHY,
    TWO_FACTOR_AUTH_TYPE_EMAIL,
    TWO_FACTOR_AUTH_TYPE_SMS
} from "../../../common/util/util-consts";
import {classNames} from "../../../common/util/util-helpers";
import InformationCircleIcon from "@heroicons/react/24/outline/InformationCircleIcon";
import Tooltip from "../../../common/components/tooltip";
import InfoBar from "../../../common/components/info-paragraph/info-bar";
import {LoaderSmall} from "../../../common/components/loader";
import {CheckCircleIcon} from "@heroicons/react/20/solid";
import ModalConfirm from "../../../common/components/modal/modal-confirm";
import {ArrowPathIcon} from "@heroicons/react/24/outline";

export default function TwoFactorAuth({logoURL, translate}) {
    const codeRef = useRef();
    const resendTimeReset = useRef(false);
    const prevUserState = useRef({});
    const user = useSelector(state => state.user);
    const data = user.data ?? {}
    const isLoading = user.isLoading;
    const dispatch = useDispatch();
    const [fields, setFields] = useState(() => getFields());
    const [showMoreOptions, setShowMoreOptions] = useState(false);
    const [tfaId, setTfaId] = useState(undefined);
    const [username, setUsername] = useState(undefined);
    const [twoFactorAuthType, setTwoFactorAuthType] = useState(undefined);
    const [TwoFactorAuthTypeAuthy, setTwoFactorAuthTypeAuthy] = useState(false);
    const [count, setCount] = useState(0);
    const [isExpiredDialogOpen, setIsExpiredDialogOpen] = useState(false);

    const moreOptionButtons = [
        {
            name: "TwoFactorAuthTypeEmail",
            resend: true,
            label: twoFactorAuthType === TWO_FACTOR_AUTH_TYPE_EMAIL ? "Resend 6 digit code to email" : "Send 6 digit code to email",
            type: TWO_FACTOR_AUTH_TYPE_EMAIL,
            isSelected: twoFactorAuthType === TWO_FACTOR_AUTH_TYPE_EMAIL,
            isDisabled: false
        },
        // {
        //     name: "TwoFactorAuthTypeSMS",
        //     resend: false,
        //     type: TWO_FACTOR_AUTH_TYPE_SMS,
        //     isDisabled: !data.TwoFactorAuthTypeSMS
        // },
        {
            name: "TwoFactorAuthTypeAuthy",
            resend: false,
            isSelected: twoFactorAuthType === TWO_FACTOR_AUTH_TYPE_AUTHY,
            type: TWO_FACTOR_AUTH_TYPE_AUTHY,
            isDisabled: !TwoFactorAuthTypeAuthy
        }
    ]

    function getFields() {
        return {
            Code: new Field('Code', '', [''], false, 'text', {
                innerRef: codeRef,
                autoFocus: true,
                max: 6,
                placeholder: "XXXXXX",
                hideLabel: true,
                addClass: "text-center"
            })
        }
    }

    const handleInputChange = (name, value) => {
        fields[name].errorMessage = "";
        setFields(FieldsManager.updateField(fields, name, value))
    }

    function handleVerifyClick() {
        dispatch(verifyToken({
            resource: "token/verify",
            params: {
                tfaId: tfaId,
                code: fields.Code.value,
                username: username,
                TwoFactorAuthType: twoFactorAuthType
            }
        }));
    }

    function handleResendClick(it) {
        dispatch(resendToken({
            resource: "token/resend",
            params: {
                tfaId: tfaId,
                username: username,
                TwoFactorAuthType: it.type
            }
        }));

        setTwoFactorAuthType(it.type);

        if (it.type === TWO_FACTOR_AUTH_TYPE_EMAIL) {
            resendTimeReset.current = true;
        }
    }

    useEffect(() => {
        if (user.data.TwoFactorAuthType === TWO_FACTOR_AUTH_TYPE_EMAIL) {
            setCount(30);
            resendTimeReset.current = false;
        }

        setTimeout(() => {
            setIsExpiredDialogOpen(true);
        }, 600000);
    }, []);

    useEffect(() => {
        if (data.tfaId) {
            setTfaId(data.tfaId);
        }

        if (data.username) {
            setUsername(data.username);
        }

        if (data.TwoFactorAuthType) {
            setTwoFactorAuthType(data.TwoFactorAuthType);
        }

        if (data.TwoFactorAuthTypeAuthy) {
            setTwoFactorAuthTypeAuthy(true)
        }
    }, [data]);

    useEffect(() => {
        if (!!prevUserState?.current?.isLoading && !user.isLoading && resendTimeReset.current) {
            setCount(30);
            resendTimeReset.current = false;
        }

        prevUserState.current = user;
    }, [user])

    useEffect(() => {
        if (count <= 0) return;

        const timer = setInterval(() => {
            setCount((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [count]);

    useEffect(() => {
        if (codeRef.current && !isLoading) {
            codeRef.current.focus();
        }
    }, [isLoading]);

    return <div>
        <img
            className="h-14 w-auto"
            src={logoURL}
            alt="Company logo"
        />

        <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight">
            Enter 6 digit code
        </h2>

        <div className="max-w-xs space-y-6">
            <form
                className="space-y-6"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (fields.Code.value.length === 6 && !isLoading) {
                        handleVerifyClick();
                    }
                }}
            >
                <FieldsForm
                    fieldsState={fields}
                    onChange={handleInputChange}
                    isFormDisabled={isLoading}
                    translate={translate}
                />

                {twoFactorAuthType === TWO_FACTOR_AUTH_TYPE_EMAIL && (
                    <InfoBar>
                        <p>Please check your email for 6 digit code. Code is valid for 10 minutes.</p>
                    </InfoBar>
                )}

                {twoFactorAuthType === TWO_FACTOR_AUTH_TYPE_SMS && (
                    <InfoBar>
                        <p>Please check your phone SMS inbox for 6 digit code code. Code is valid for 10 minutes.</p>
                    </InfoBar>
                )}

                {twoFactorAuthType === TWO_FACTOR_AUTH_TYPE_AUTHY && (
                    <InfoBar>
                        <p>Please enter your Authenticator app 6 digit code in the next 10 minutes.</p>
                    </InfoBar>
                )}

                {user.errorMessage && (
                    <InfoBar type="danger">
                        {translate("text." + user.errorMessage + "_AUTH")}
                    </InfoBar>
                )}


                <div className="flex">
                    <Button
                        disabled={isLoading || fields.Code.value.length !== 6}
                        onClick={handleVerifyClick}
                        appearance="primary"
                        addClass="w-full justify-center bg-green-600 hover:enabled:bg-green-700"
                    >
                    <span className="px-7 relative">
                        {isLoading && (
                            <LoaderSmall addClass="absolute left-0"/>
                        )}

                        {translate('btn.verify')}
                     </span>
                    </Button>
                </div>
            </form>

            <div className="flex mt-6">
                <Button
                    onClick={() => setShowMoreOptions(!showMoreOptions)}
                    addClass="w-full justify-center"
                >
                    Sign in another way <ChevronDownIcon
                    className={classNames("size-5 ml-1", showMoreOptions ? "rotate-180" : undefined)}/>
                </Button>
            </div>

            {showMoreOptions && (
                <div className="border-t-2 border-tm-gray-300 flex flex-col gap-4 mt-6 pt-6">
                    {moreOptionButtons
                        .map(it => {
                            return <div key={it.name} className="relative">
                                <Button
                                    disabled={it.isDisabled || (it.resend && count) || (it.isSelected && twoFactorAuthType === TWO_FACTOR_AUTH_TYPE_AUTHY)}
                                    onClick={() => handleResendClick(it)}
                                    addClass="w-full justify-center"
                                    appearance={it.isSelected ? "primary" : undefined}
                                >
                                    {it.label
                                        ? it.label
                                        : translate("btn." + it.name)
                                    }

                                    {it.resend && !!count && (
                                        <div className="absolute right-4">{count}</div>
                                    )}
                                </Button>

                                {it.isSelected && (
                                    <CheckCircleIcon
                                        className="size-5 absolute left-4 top-2 text-primary bg-inverse rounded-full"/>
                                )}

                                {it.isDisabled && (
                                    <Tooltip content="This option is not available because it hasn’t been set up yet. Please visit 'My profile' page to complete setup."
                                             trigger={'focus mouseenter click'}>
                                        <button className="absolute right-4 top-2 rounded-btn"><InformationCircleIcon
                                            className="size-5 text-tm-gray-500"/>
                                        </button>
                                    </Tooltip>
                                )}
                            </div>
                        })}
                </div>
            )}
        </div>

        <ModalConfirm
            type={"warning"}
            title={'The page was only valid for 10 minutes'}
            show={isExpiredDialogOpen}
            buttonLabel={<div className="flex gap-2"><ArrowPathIcon className="size-5"/> Refresh page</div>}
            onConfirm={() => {
                window.location.reload();
            }}
            translate={translate}
        >
            Please refresh it and try again.
        </ModalConfirm>
    </div>
}